import {
  Building,
  CircleCheckBig,
  Factory,
  Mail,
  Monitor,
  Store,
  ToggleLeft,
  Zap,
} from "lucide-react";
import CompanyProfile from "@/features/onboarding/components/Onboarding/CompanyProfile";
import DefinePitch from "@/features/onboarding/components/Onboarding/DefinePitch";
import DefineAudience from "@/features/onboarding/components/Onboarding/Tailored/DefineAudience";
import TrainTrainer from "@/features/onboarding/components/Onboarding/Tailored/TrainTrainer";
import Integration from "@/shared/components/ui/Connection/Integrations";

import AiIcon from "@/assets/svg/Ai-Icon";
import Logo from "@/assets/images/BrandLogo_small.png";
export const onBoardingOptions = [
  {
    id: 1,
    title: "Quick Onboarding",
    description: "Get started fast! Set up with essential info.",
    time: "1-2 mins",
    icon: <Zap color="#207C97" size={22} />,
    recommended: false,
  },
  {
    id: 2,
    title: "Tailored Onboarding",
    description:
      "Customize your setup for better results. Tailor it to your needs.",
    time: "5-6 mins",
    icon: <ToggleLeft color="#207C97" size={22} />,
    recommended: true,
  },
];

export const QuickOnboardingSteps = [
  {
    id: 1,
    title: "Company Profile",
    rightSideData: {
      form: (props) => <CompanyProfile {...props} />,
      title: "What Does Your Company Do?",
      description:
        "We've added some details to get you started. Feel free to tweak and make it yours!",
    },
  },
  {
    id: 2,
    title: "Integrations",
    rightSideData: {
      title: "Connect Your Tools",
      description:
        "Connect your communication tools to make every message count.",
      form: (props) => <Integration {...props} />,
    },
  },
];

export const TailoredOnboardingSteps = [
  {
    id: 1,
    title: "Company Profile",
    rightSideData: {
      title: "What Does Your Company Do?",
      description:
        "We've added some details to get you started. Feel free to tweak and make it yours!",
      form: (props) => <CompanyProfile {...props} />,
    },
  },
  {
    id: 2,
    title: "Define Pitch",
    rightSideData: {
      title: "Let's Fine Tune Your Pitch",
      description: "Take a moment to review and personalize your pitch.",
      form: (props) => <DefinePitch {...props} />,
    },
  },
  {
    id: 3,
    title: "Define Your Audience",
    rightSideData: {
      title: "Define Your Ideal Audience",
      description:
        "This information helps us identify the best opportunities for your business.",
      selectWithAI: true,
      form: (props) => <DefineAudience {...props} />,
    },
  },
  {
    id: 4,
    title: "Train The Trainer",
    rightSideData: {
      title: "Train The Trainer - Optional",
      description:
        "Help SalesWorx learn your communication style for more personalized email outreach.",
      padding: "pt-4",
      form: (props) => <TrainTrainer {...props} />,
    },
  },
  {
    id: 5,
    title: "Integrations",
    rightSideData: {
      title: "Connect Your Tools",
      description:
        "Connect your communication tools to make every message count.",
      form: (props) => <Integration {...props} />,
    },
  },
];

/** Canonical company size ranges used on the API. */
export const COMPANY_SIZE_RANGES = ["1-50", "51-200", "201-500", "500+"];

/** Legacy slug → canonical range (for data written before the hybrid switch). */
export const COMPANY_SIZE_SLUG_TO_RANGE = {
  small: "1-50",
  medium: "51-200",
  large: "201-500",
  enterprise: "500+",
};

/** Range → human-readable label, for display surfaces. */
export const COMPANY_SIZE_LABEL_BY_RANGE = {
  "1-50": "Small",
  "51-200": "Medium",
  "201-500": "Large",
  "500+": "Enterprise",
};

/**
 * Normalize any stored company-size token (legacy slug or range) to the
 * canonical range string. Returns null for unknown/empty input so callers can
 * filter garbage out of legacy arrays.
 */
export function normalizeCompanySize(value) {
  if (value == null) return null;
  const str = String(value).trim();
  if (!str) return null;
  if (COMPANY_SIZE_RANGES.includes(str)) return str;
  const mapped = COMPANY_SIZE_SLUG_TO_RANGE[str.toLowerCase()];
  return mapped ?? null;
}

/** Normalize an array (or scalar) of sizes to a deduped array of canonical ranges. */
export function normalizeCompanySizeList(input) {
  const list = Array.isArray(input) ? input : input ? [input] : [];
  const out = [];
  for (const raw of list) {
    const norm = normalizeCompanySize(raw);
    if (norm && !out.includes(norm)) out.push(norm);
  }
  return out;
}

export const BUSINESS_SIZES = [
  {
    label: "Small Business",
    subLabel: "1-50 employees",
    value: "1-50",
    icon: <Store size={24} />,
  },
  {
    label: "Medium Business",
    subLabel: "51-200 employees",
    value: "51-200",
    icon: <Building size={24} />,
  },
  {
    label: "Large Business",
    subLabel: "201-500 employees",
    value: "201-500",
    icon: <Building size={24} />,
  },
  {
    label: "Enterprise",
    subLabel: "500+ employees",
    value: "500+",
    icon: <Factory size={24} />,
  },
];

export const targetIndustriesOptions = [
  // Technology
  "Software / SaaS",
  "Hardware / Semiconductors",
  "Cloud & Infrastructure",
  "Cybersecurity",
  "Artificial Intelligence / ML",
  "IT Services & Consulting",
  "Telecommunications",
  "Consumer Electronics",
  "Data & Analytics",
  "ERP / Enterprise Software",

  // Manufacturing & Industrial
  "Automotive Manufacturing",
  "Aerospace & Defense",
  "Industrial Machinery & Equipment",
  "Electronics Manufacturing",
  "Chemical Manufacturing",
  "Pharmaceutical Manufacturing",
  "Food & Beverage Manufacturing",
  "Metals & Mining",
  "Plastics & Rubber",
  "Textile & Apparel Manufacturing",
  "Paper & Packaging",
  "Heavy Industry / Engineering",

  // Financial Services
  "Banking",
  "Investment Banking",
  "Insurance",
  "Asset Management / PE / VC",
  "Fintech",
  "Payments",
  "Capital Markets",
  "Microfinance",
  "Accounting & Audit Services",

  // Healthcare & Life Sciences
  "Hospitals & Health Systems",
  "Pharmaceuticals",
  "Medical Devices",
  "Biotechnology",
  "Health Insurance",
  "Digital Health / HealthTech",
  "Clinical Research",
  "Diagnostics & Pathology",

  // Consumer & Retail
  "E-Commerce",
  "Retail",
  "FMCG / CPG",
  "Luxury Goods & Fashion",
  "Grocery & Supermarkets",
  "Sports & Fitness",
  "Consumer Electronics Retail",

  // Energy & Utilities
  "Oil & Gas",
  "Renewable Energy / Cleantech",
  "Electric Utilities",
  "Power Generation",
  "Water & Waste Management",
  "Nuclear Energy",

  // Professional Services
  "Management Consulting",
  "Legal Services",
  "Marketing & Advertising",
  "HR & Recruitment",
  "Engineering Consulting",
  "Research & Analytics",
  "IT / Digital Consulting",

  // Government & Public Sector
  "Government",
  "Public Sector",
  "Defence & Military",
  "Public Healthcare",
  "Non-Profit / NGO",

  // Real Estate & Construction
  "Commercial Real Estate",
  "Residential Real Estate",
  "Construction & Infrastructure",
  "Property Management",
  "Architecture & Design",

  // Logistics & Supply Chain
  "Freight & Shipping",
  "Warehousing & Distribution",
  "Third-Party Logistics (3PL)",
  "Last-Mile Delivery",
  "Supply Chain Management",
  "Port & Terminal Operations",

  // Media & Entertainment
  "Broadcasting",
  "Film & Production",
  "Gaming",
  "Streaming & OTT",
  "Publishing",
  "Music & Entertainment",
  "Digital Media / Ad Tech",

  // Education
  "K-12 Education",
  "Higher Education",
  "EdTech",
  "Corporate Training & L&D",
  "Vocational & Skills Training",

  // Travel & Hospitality
  "Airlines",
  "Hotels & Resorts",
  "Travel Agencies & OTAs",
  "Cruise Lines",
  "Restaurant & Food Services",
  "Event Management",

  // Agriculture
  "Agribusiness / Farming",
  "AgriTech",
  "Food Processing",
  "Agricultural Equipment",
  "Animal Husbandry"
];

export const roleOptions = [
  // C-Level
  "Chief Executive Officer",
  "Chief Operating Officer",
  "Chief Financial Officer",
  "Chief Technology Officer",
  "Chief Information Officer",
  "Chief Marketing Officer",
  "Chief Information Security Officer",
  "Chief Digital Officer",
  "Chief Product Officer",
  "Chief Revenue Officer",
  "Chief Legal Officer",
  "Chief Human Resources Officer",
  "Chief Customer Officer",
  "Chief Strategy Officer",
  "Chief Analytics Officer",

  // VP Level
  "VP of Sales",
  "VP of Marketing",
  "VP of Engineering",
  "VP of Product",
  "VP of Operations",
  "VP of Finance",
  "VP of Customer Success",
  "VP of Business Development",
  "VP of HR / People",

  // Director Level
  "Director of Sales",
  "Director of Marketing",
  "Director of IT",
  "Director of Operations",
  "Director of Product Management",
  "Director of Finance",
  "Director of Customer Experience",
  "Director of Data & Analytics",
  "Director of Procurement",

  // Manager Level
  "Sales Manager",
  "Marketing Manager",
  "Product Manager",
  "Engineering Manager",
  "Account Manager",
  "IT Manager",
  "Operations Manager",
  "Finance Manager",
  "HR Manager",
  "Supply Chain Manager",
  "Customer Success Manager",
  "Project Manager",
  "Revenue Operations Manager",

  // Individual Contributors
  "Business Analyst",
  "Data Analyst",
  "Solutions Architect",
  "Pre-Sales Consultant",
  "Sales Executive",
  "Account Executive",
  "SDR",
  "BDR",
  "Marketing Executive",
  "Software Engineer",
  "Data Scientist",
  "Financial Analyst",
  "HR Business Partner",
  "Customer Success Associate",
  "Technical Support Engineer",
  "UX Designer",
  "Content Strategist",
  "SEO Specialist",
  "Procurement Specialist",

  // Others
  "Board Member",
  "Independent Director",
  "Advisor",
  "Mentor",
  "Investor",
  "VC Partner",
  "Angel Investor"
];

export const countryOptions = [
  // North America
  "United States",
  "Canada",
  "Mexico",

  // Europe
  "United Kingdom",
  "Germany",
  "France",
"Netherlands",
  "Belgium",
  "Switzerland",
  "Sweden",
  "Norway",
  "Denmark",
  "Finland",
  "Ireland",
  "Austria",
  "Spain",
  "Portugal",
  "Italy",
  "Poland",
  "Czech Republic",
  "Hungary",
  "Romania",
  "Ukraine",
  "Serbia",
  "Slovakia",
  "Croatia",
  "Bulgaria",

  // Middle East
  "United Arab Emirates",
  "Saudi Arabia",
  "Qatar",
  "Bahrain",
  "Kuwait",
  "Oman",
  "Israel",
  "Turkey",

  // Africa
  "South Africa",
  "Egypt",
  "Nigeria",
  "Kenya",
  "Morocco",
  "Ethiopia",
  "Ghana",

  // South Asia
  "India",
  "Pakistan",
  "Bangladesh",
  "Sri Lanka",
  "Nepal",

  // Southeast Asia
  "Singapore",
  "Indonesia",
  "Malaysia",
  "Thailand",
  "Vietnam",
  "Philippines",
  "Myanmar",

  // East Asia
  "China",
  "Japan",
  "South Korea",
  "Hong Kong SAR",
  "Taiwan",

  // Oceania
  "Australia",
  "New Zealand",

  // South America
  "Brazil",
  "Argentina",
  "Colombia",
  "Chile",
  "Peru",
  "Ecuador",
  "Venezuela",
  "Uruguay"
];

export const businessInterest = [
  "Market Expansion",
  "Scaling Operations",
  "International Growth",
  "Strategic Partnerships",
  "Mergers & Acquisitions",
  "Investment Opportunities",
];

/** Company- and contact-level sales triggers with examples for ChipInput tooltips. */
export const companyLevelTriggerOptions = [
  {
    label: "Growth Strategy",
    value: "Growth Strategy",
    description: "Plans to expand in a specific region or services.",
  },
  {
    label: "New Executive Hires",
    value: "New Executive Hires",
    description: "New CEO, CMO, or VP of Sales hired.",
  },
  {
    label: "Funding Rounds",
    value: "Funding Rounds",
    description: "Series A, B, C, or IPO announcements.",
  },
  {
    label: "Customer Churn",
    value: "Customer Churn",
    description: "High churn rates or customer dissatisfaction.",
  },
  {
    label: "Sales Targets/Vision",
    value: "Sales Targets/Vision",
    description: "Public revenue, growth, or market-share targets.",
  },
  {
    label: "Low Revenue Growth",
    value: "Low Revenue Growth",
    description: "Stagnating growth or profitability.",
  },
  {
    label: "Negative Press",
    value: "Negative Press",
    description: "PR crises or operational issues.",
  },
  {
    label: "Mergers & Acquisitions (M&A)",
    value: "Mergers & Acquisitions (M&A)",
    description: "Two companies announce a merger or acquisition.",
  },
  {
    label: "Expansion",
    value: "Expansion",
    description: "Opening new offices or entering new markets.",
  },
  {
    label: "Product or Service Launch",
    value: "Product or Service Launch",
    description: "Launching a new product or service.",
  },
  {
    label: "Partnerships",
    value: "Partnerships",
    description: "Collaborations with other organizations.",
  },
  {
    label: "Layoffs or Restructuring",
    value: "Layoffs or Restructuring",
    description: "Downsizing or leadership reorganization.",
  },
  {
    label: "Increased Hiring",
    value: "Increased Hiring",
    description:
      "Hiring a large number of employees in a specific department.",
  },
  {
    label: "RFP Announcements",
    value: "RFP Announcements",
    description: "Request for proposals for a specific need.",
  },
  {
    label: "Tech Stack Changes",
    value: "Tech Stack Changes",
    description: "Migrating to Salesforce or adopting a new CRM.",
  },
  {
    label: "Infrastructure Downtime",
    value: "Infrastructure Downtime",
    description: "Cybersecurity breaches or service outages.",
  },
  {
    label: "Scalability Issues",
    value: "Scalability Issues",
    description: "Struggles with growing operations.",
  },
  {
    label: "Promotions or Role Changes",
    value: "Promotions or Role Changes",
    description: "Prospect becomes VP of Operations or similar.",
  },
  {
    label: "Content Engagement",
    value: "Content Engagement",
    description: "Interaction with your blogs, webinars, or newsletters.",
  },
  {
    label: "Social Media Activity",
    value: "Social Media Activity",
    description: "Prospect posts about a challenge on LinkedIn.",
  },
  {
    label: "Conference Attendance",
    value: "Conference Attendance",
    description: "Attended an industry event.",
  },
  {
    label: "Connections to Your Network",
    value: "Connections to Your Network",
    description: "Prospect has mutual connections with your clients.",
  },
  {
    label: "Website Visits",
    value: "Website Visits",
    description: "Visiting product or pricing pages.",
  },
  {
    label: "Email Engagement",
    value: "Email Engagement",
    description: "Opening emails or clicking on links.",
  },
  {
    label: "Demo Requests",
    value: "Demo Requests",
    description: "Requesting a product demo.",
  },
  {
    label: "Content Downloads",
    value: "Content Downloads",
    description: "Downloading whitepapers, case studies, or reports.",
  },
];

/** Industry-level triggers with examples for ChipInput tooltips. */
export const industryLevelTriggerOptions = [
  {
    label: "Regulatory Changes",
    value: "Regulatory Changes",
    description: "New compliance requirements in the industry.",
  },
  {
    label: "Industry Use Cases",
    value: "Industry Use Cases",
    description: "Aftermarket sales boost, e-commerce improvement, and similar trends.",
  },
  {
    label: "Economic Shifts",
    value: "Economic Shifts",
    description: "Recessions, inflation, or market downturns.",
  },
  {
    label: "Technological Trends",
    value: "Technological Trends",
    description: "Growth in AI, cloud adoption, or automation.",
  },
  {
    label: "Negative Press",
    value: "Negative Press",
    description: "Industry-wide reputational or operational issues in the news.",
  },
  {
    label: "Funding Announcements",
    value: "Funding Announcements",
    description: "Sector peers announcing new funding rounds.",
  },
  {
    label: "Mergers & Acquisitions",
    value: "Mergers & Acquisitions",
    description: "Consolidation activity across the industry.",
  },
  {
    label: "Market Expansion",
    value: "Market Expansion",
    description: "Competitors or peers entering new geographies.",
  },
  {
    label: "New Product Launches",
    value: "New Product Launches",
    description: "Industry players releasing new offerings.",
  },
  {
    label: "Partnership Announcements",
    value: "Partnership Announcements",
    description: "Strategic alliances announced in the sector.",
  },
  {
    label: "Hiring Trends",
    value: "Hiring Trends",
    description: "Rising demand for roles or skills in the industry.",
  },
  {
    label: "Layoffs or Downsizing",
    value: "Layoffs or Downsizing",
    description: "Widespread workforce reductions in the sector.",
  },
  {
    label: "Compliance Updates",
    value: "Compliance Updates",
    description: "New standards or audits affecting the industry.",
  },
  {
    label: "Competitive Activity",
    value: "Competitive Activity",
    description: "Aggressive moves from key players in the market.",
  },
  {
    label: "Digital Transformation",
    value: "Digital Transformation",
    description: "Industry shift toward digital-first operations.",
  },
  {
    label: "AI Adoption",
    value: "AI Adoption",
    description: "Accelerating use of AI across the industry.",
  },
  {
    label: "Security Breaches",
    value: "Security Breaches",
    description: "High-profile security incidents in the sector.",
  },
  {
    label: "Industry Events",
    value: "Industry Events",
    description: "Major conferences, summits, or trade shows.",
  },
  {
    label: "Policy Changes",
    value: "Policy Changes",
    description: "Government or trade policy updates affecting the sector.",
  },
  {
    label: "Supply Chain Disruptions",
    value: "Supply Chain Disruptions",
    description: "Logistics or supplier issues impacting the industry.",
  },
  {
    label: "Sustainability Initiatives",
    value: "Sustainability Initiatives",
    description: "ESG and green initiatives gaining traction.",
  },
  {
    label: "Cloud Migration Trends",
    value: "Cloud Migration Trends",
    description: "Move to cloud infrastructure across the industry.",
  },
  {
    label: "Automation Adoption",
    value: "Automation Adoption",
    description: "Broader rollout of process automation.",
  },
  {
    label: "Data Privacy Regulations",
    value: "Data Privacy Regulations",
    description: "Privacy laws shaping how data is handled.",
  },
  {
    label: "Customer Experience Trends",
    value: "Customer Experience Trends",
    description: "Shifting expectations for CX in the industry.",
  },
  {
    label: "Pricing Model Changes",
    value: "Pricing Model Changes",
    description: "Subscription, usage-based, or other pricing shifts.",
  },
  {
    label: "Global Trade Changes",
    value: "Global Trade Changes",
    description: "Tariffs, trade agreements, or cross-border impacts.",
  },
  {
    label: "Industry Consolidation",
    value: "Industry Consolidation",
    description: "Fewer, larger players through mergers and exits.",
  },
  {
    label: "Innovation Trends",
    value: "Innovation Trends",
    description: "Emerging technologies reshaping the sector.",
  },
  {
    label: "Emerging Markets Expansion",
    value: "Emerging Markets Expansion",
    description: "Growth focus on emerging economies.",
  },
];

/** Used for Set lookups and legacy strings. */
export const triggers = companyLevelTriggerOptions.map((o) => o.value);

/** Used for Set lookups and legacy strings. */
export const industryLevelTriggers = industryLevelTriggerOptions.map(
  (o) => o.value,
);

export const AdminRequestProcessingSteps = [
  {
    icon: <Mail className="text-gray-500" size={18} />,
    title: "Email Notification",
    description:
      "An approval request has been sent to the SalesWorx admin team. You'll receive an email as soon as your workspace is approved.",
  },
  {
    icon: <CircleCheckBig className="text-gray-500" size={18} />,
    title: "Approval Process",
    description: "Our team typically reviews new workspaces within 24 hours.",
  },
];

// website analysis utils
export function normalizeWebsiteUrl(raw) {
  const t = String(raw || "").trim();
  if (!t) return "";
  if (/^https?:\/\//i.test(t)) return t;
  return `https://${t}`;
}

/** e.g. https://www.worxwide.com → worxwide.com */
export function hostnameFromUrl(url) {
  try {
    return new URL(url).hostname.replace(/^www\./, "") || "";
  } catch {
    return "";
  }
}

/** e.g. worxwide.com → Worxwide */
export function brandLabelFromHost(host) {
  if (!host) return "your company";
  const part = host.split(".")[0];
  if (!part) return host;
  return part.charAt(0).toUpperCase() + part.slice(1);
}


// llm options

export const LLM_OPTIONS = [
  {
    id: "product_llm",
    name: "SalesWorx AI",
    description: "The best AI model for sales engagement.",
    icon: <img src={Logo} alt="SalesWorx AI" className="w-5 h-5 object-contain" />,
  },
  {
    id: "bring_your_own_llm",
    name: "Bring Your Own LLM",
    description: "Use your own LLM model to generate content.",
    icon: <AiIcon className="text-success-500" size={20} />,
  },
];

export const LLM_PROVIDERS = [
  {
    id: "openai",
    name: "OpenAI",
    description: "OpenAI is a cloud-based AI platform that provides a range of AI models.",
    icon: <Monitor className="text-secondary" size={20} />,
  },
  {
    id: "anthropic",
    name: "Anthropic",
    description: "Anthropic is a cloud-based AI platform that provides a range of AI models.",
    icon: <Monitor className="text-secondary" size={20} />,
    disabled: true,
  },
];